import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navLinks } from '../mock';
import { useAuth } from '../context/AuthContext';

const SCROLL_THRESHOLD_PX = 20;

function BrandLogo() {
  return (
    <Link to="/" className="flex items-center gap-3 group">
      <img
        src="/brand/app-icon.png"
        alt="LISTING AiGENT logo"
        width="44"
        height="44"
        className="w-11 h-11 rounded-full object-cover shadow-md transition-transform duration-300 group-hover:scale-105"
      />
      <div className="leading-none">
        <span className="font-serif text-brand-cream text-lg tracking-wide">
          LISTING <span className="relative">A<span className="text-brand-gold">i</span>GENT</span>
        </span>
      </div>
    </Link>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden md:flex items-center gap-1">
      {navLinks.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === '/'}
          className={({ isActive }) =>
            `px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded-md text-brand-cream hover:text-brand-gold ${
              isActive ? 'text-brand-gold' : ''
            }`
          }
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}

function displayName(user) {
  if (!user) return '';
  if (user.name) return user.name;
  if (user.email) return user.email.split('@')[0];
  return 'there';
}

function DesktopCTA() {
  const { user, clearSession } = useAuth();
  if (user) {
    return (
      <div className="hidden md:flex items-center gap-4">
        <span data-testid="navbar-welcome" className="text-sm font-medium text-brand-cream">
          Welcome, <span className="text-brand-gold">{displayName(user)}</span>!
        </span>
        <Link to="/crm" data-testid="navbar-crm" className="text-sm font-medium text-brand-cream hover:text-brand-gold transition-colors">CRM</Link>
        <Link to="/billing" data-testid="navbar-billing" className="text-sm font-medium text-brand-cream hover:text-brand-gold transition-colors">Billing</Link>
        {user.role === 'admin' && (
          <Link to="/admin" data-testid="navbar-admin" className="text-sm font-medium text-brand-cream hover:text-brand-gold transition-colors">Admin</Link>
        )}
        <Link to="/settings" data-testid="navbar-settings" className="text-sm font-medium text-brand-cream hover:text-brand-gold transition-colors">Settings</Link>
        <button type="button" onClick={clearSession} data-testid="navbar-signout"
          className="px-5 py-2.5 rounded-lg bg-brand-cream text-brand-green text-sm font-semibold tracking-wide transition-transform duration-200 hover:scale-[1.03] hover:shadow-lg">
          Sign out
        </button>
      </div>
    );
  }
  return (
    <div className="hidden md:flex items-center gap-3">
      <Link to="/login" data-testid="navbar-signin" className="text-sm font-medium text-brand-cream hover:text-brand-gold transition-colors">
        Sign in
      </Link>
      <Link to="/download" data-testid="navbar-trial"
        className="px-5 py-2.5 rounded-lg bg-brand-cream text-brand-green text-sm font-semibold tracking-wide transition-transform duration-200 hover:scale-[1.03] hover:shadow-lg">
        Start 7-day trial
      </Link>
    </div>
  );
}

function MobileMenu() {
  const { user, clearSession } = useAuth();
  return (
    <div className="md:hidden bg-brand-green-dark border-t border-brand-cream/10 animate-fade-in-up">
      <nav className="px-5 py-4 flex flex-col gap-1">
        {user && (
          <div data-testid="navbar-welcome-mobile" className="px-4 py-3 text-brand-cream border-b border-brand-cream/10 mb-2">
            Welcome, <span className="text-brand-gold font-semibold">{displayName(user)}</span>!
          </div>
        )}
        {navLinks.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            className={({ isActive }) =>
              `px-4 py-3 rounded-md text-sm font-medium ${
                isActive ? 'bg-brand-green text-brand-cream' : 'text-brand-cream/80'
              }`
            }
          >
            {link.label}
          </NavLink>
        ))}
        {user ? (
          <>
            <Link to="/crm" data-testid="navbar-crm-mobile"
              className="mt-2 px-4 py-3 text-center rounded-lg border border-brand-cream/30 text-brand-cream font-medium">
              CRM
            </Link>
            <Link to="/billing" data-testid="navbar-billing-mobile"
              className="mt-2 px-4 py-3 text-center rounded-lg border border-brand-cream/30 text-brand-cream font-medium">
              Billing
            </Link>
            {user.role === 'admin' && (
              <Link to="/admin" data-testid="navbar-admin-mobile"
                className="mt-2 px-4 py-3 text-center rounded-lg border border-brand-cream/30 text-brand-cream font-medium">
                Admin
              </Link>
            )}
            <Link to="/settings" data-testid="navbar-settings-mobile"
              className="mt-2 px-4 py-3 text-center rounded-lg border border-brand-cream/30 text-brand-cream font-medium">
              Settings
            </Link>
            <button type="button" onClick={clearSession} data-testid="navbar-signout-mobile"
              className="px-4 py-3 text-center rounded-lg bg-brand-cream text-brand-green font-semibold">
              Sign out
            </button>
          </>
        ) : (
          <>
            <Link to="/login" data-testid="navbar-signin-mobile"
              className="mt-2 px-4 py-3 text-center rounded-lg border border-brand-cream/30 text-brand-cream font-medium">
              Sign in
            </Link>
            <Link to="/download" data-testid="navbar-trial-mobile"
              className="px-4 py-3 text-center rounded-lg bg-brand-cream text-brand-green font-semibold">
              Start 7-day trial
            </Link>
          </>
        )}
      </nav>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // setState setters are stable; SCROLL_THRESHOLD_PX is a module-level constant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Intentionally only re-runs when the route changes; setOpen is a stable setter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // setOpen is stable per React; `prev` is the functional-update parameter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleMenu = useCallback(() => setOpen((prev) => !prev), []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 bg-brand-green ${
        scrolled ? 'shadow-lg shadow-black/20' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <BrandLogo />
          <DesktopNav />
          <DesktopCTA />
          <button
            className="md:hidden text-brand-cream p-2"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && <MobileMenu />}
    </header>
  );
}
